import { supplierStockCost } from '@/lib/supplier-stock-cost';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { storableTaxLineType } from '@/lib/sales/tax-line-type';
import {
  costBasisFromPrimarySupplier,
  pricingAfterMirroredCost,
} from '@/lib/sales/line-item-pricing';

// Re-exported so this route's own tests and callers are unchanged; the v2
// form imports them from the lib directly.
export { costBasisFromPrimarySupplier, pricingAfterMirroredCost };
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Boxes, Crown, Loader2, Package, Receipt, Wrench, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Form } from '@/components/atoms/form';
import { TooltipProvider } from '@/components/atoms/tooltip';
import { FormPageLayout } from '@/components/layouts';
import { FormSidebar, FormSidebarSection, UnsavedChangesDialog } from '@/components/molecules';
import {
  PricebookItemAdvancedSections,
  PricebookItemMainSection,
  type PricebookComponentItemCache,
} from '@/components/organisms/pricebook/PricebookItemFormSections';
import { categoryFamilyForItemType } from '@/components/organisms/pricebook/support/category-family';
import { useComponentTotals } from '@/hooks/pricebook/useComponentTotals';
import { useCategoryList } from '@/hooks/pricebook/usePricebookCategories';
import { MembershipPricingBreakdown } from '@/components/organisms/pricebook/MembershipPricingBreakdown';
import {
  flatServiceCostBreakdown,
  summedBundlePrice,
} from '@/components/organisms/pricebook/support/pricebook-bundle';
import { previewOptionalAdjustments } from '@/lib/sales/dynamic-service-price';
import { flatServiceLaborLines } from '@/lib/sales/service-labor';
import { Switch } from '@/components/atoms/switch';
import {
  laborLinesHours,
  laborLinesTotal,
} from '@/components/organisms/pricebook/LaborLinesEditor';
import { useApplyDynamicPrice, useLaborRateProfiles } from '@/hooks/pricebook/useDynamicPricing';
import type { DraftItemSupplier } from '@/components/organisms/pricebook/ItemSuppliersSection';
import { useAttachSupplier } from '@/hooks/pricebook/useSuppliers';
import {
  useCreateEquipmentDescription,
  useUpdateEquipmentDescription,
} from '@/hooks/equipment/useEquipmentDescriptions';
import { ensureEquipmentType, newEquipmentTypeSpec } from '@/lib/pricebook/equipment-type';
import { DocumentCostPanel } from '@/components/organisms/sales/DocumentCostPanel';
import { PricebookBundleSummary } from '@/components/organisms/pricebook/PricebookBundleSummary';
import {
  AdvancedServiceAdjustments,
  DynamicServicePricingEditor,
} from '@/components/organisms/pricebook/DynamicServicePricingEditor';
import { ProfitUpliftEditor } from '@/components/organisms/pricebook/ProfitUpliftEditor';
import { normalizeIdList, sanitizeIdList } from '@/components/organisms/pricebook/support/id-lists';
import {
  equipmentCategoryRequirement,
  pricebookItemChecklist,
} from '@/components/organisms/pricebook/support/item-checklist';
import { computePricebookSectionOpenState } from '@/components/organisms/pricebook/support/section-open-state';
import {
  fmt,
  isFlatPricePricebookService,
  isTimeAndMaterialsPricebookItem,
  pricebookPrimaryPrice,
  pricebookPrimaryPriceLabel,
  pricebookPrimaryPriceSuffix,
} from '@/components/organisms/pricebook/support/pricebook-helpers';
import { PricebookCreatePage } from '@/components/organisms/pricebook/v2/PricebookCreatePage';
import { useCreateItem } from '@/hooks/pricebook/useCreateItem';
import { useUpdateItem } from '@/hooks/pricebook/useUpdateItem';
import { usePricebookItem } from '@/hooks/pricebook/usePricebookItem';
import { useUploadPricebookImage } from '@/hooks/pricebook/useUploadPricebookImage';
import { useDynamicServiceSummary } from '@/hooks/pricebook/useDynamicServiceSummary';
import { useImagePreview } from '@/hooks/useImagePreview';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { useEquipmentDescription } from '@/hooks/equipment/useEquipmentDescriptions';
import { useBusinessUnits } from '@/hooks/scheduler/useBusinessUnits';
import { isApiError } from '@/lib/api/errors';
import { applyServerErrorsToForm } from '@/lib/forms/apply-server-errors';
import { toServiceComponentPayloads } from '@/lib/pricebook/service-component-payload';
import type {
  ItemType,
  PriceStrategy,
  ServiceComponent,
  UpsellItem,
  CreateItemDto,
  PricebookItem,
  DynamicServicePricingConfig,
  ServiceLaborLine,
} from '@/types/pricebook';
import { ITEM_TYPE_LABELS, priceStrategyForItemType } from '@/types/pricebook';
import { searchFlag } from '@/components/organisms/pricebook/v2/support/search-flags';
import { pricebookDescriptionSchema } from '@/components/organisms/pricebook/support/description-validation';

// NOT-605 — `membership` lets an operator create a sellable membership line
// (linked to a plan) that can be added to an estimate: adding it re-prices the
// cart at member rates (in-cart unlock) and is excluded from the deposit base.
// The form already carries all the membership machinery (plan link, DTO branch,
// advanced sections) — it was only gated off here.
// `fee` makes a trip/dispatch/diagnostic charge a first-class catalog item
// rather than a hand-typed one-off line. Membership waivers reference this
// structured type directly; they never depend on a tag naming convention.
const ITEM_TYPES = ['service', 'material', 'equipment', 'membership', 'bundle', 'fee'] as const;
type SelectableItemType = (typeof ITEM_TYPES)[number];

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/_dashboard/dashboard/pricebook/new')({
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    type: SelectableItemType;
    mode?: 'quick' | 'full' | 'wizard';
    inSheet?: boolean;
    duplicate?: string;
    equipmentDescriptionId?: string;
    /** Fall back to the pre-redesign form. */
    legacy?: boolean;
  } => ({
    type: ITEM_TYPES.includes(search.type as (typeof ITEM_TYPES)[number])
      ? (search.type as SelectableItemType)
      : 'service',
    mode: search.mode as 'quick' | 'full' | 'wizard' | undefined,
    inSheet: search.inSheet === true || search.inSheet === 'true',
    duplicate: typeof search.duplicate === 'string' ? search.duplicate : undefined,
    // Deep-link from a type's "Add to pricebook" — preselect + prefill from it.
    equipmentDescriptionId:
      typeof search.equipmentDescriptionId === 'string' ? search.equipmentDescriptionId : undefined,
    // `searchFlag`, not a `=== 'true'` check: a query string carries text, so
    // the `?legacy=1` this file documents three lines down arrived as the
    // STRING '1' and matched nothing. The escape hatch has never fired.
    legacy: searchFlag(search.legacy),
  }),
  component: NewPricebookItemWrapper,
});

// ─── Wrapper ──────────────────────────────────────────────────────────────────

/**
 * The redesigned create form, with the old one still reachable at `?legacy=1`.
 *
 * The new form asks what you are adding first and then shows only that type's
 * fields; the old one asked all sixty-one of every type at once. The escape
 * hatch stays until the remaining phases (duplicate seeding, the equipment
 * spec panels, suppliers) are wired onto the new tree — see
 * docs/plans/pricebook-redesign-plan.md.
 */
function NewPricebookItemWrapper() {
  const search = Route.useSearch();
  if (search.legacy) return <NewPricebookItemPage />;
  return (
    <PricebookCreatePage
      initialType={search.type}
      duplicateId={search.duplicate}
      equipmentDescriptionId={search.equipmentDescriptionId}
    />
  );
}

// ─── Schema ───────────────────────────────────────────────────────────────────

// All five the tax engine defines, and the same five the form's own selector
// offers. `freight` was missing here only: the option rendered, the operator
// picked it, and zod rejected the submit — so a freight item could not be
// created, nor an existing one duplicated, though editing one saved fine.
const TAX_LINE_TYPES = ['flat_rate', 'labor', 'materials', 'freight', 'exempt'] as const;

// Exported so the route's validation contract can be unit-tested directly.
// eslint-disable-next-line react-refresh/only-export-components
export const itemSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'Name is required')
      .max(255, 'Name must be 255 characters or fewer'),
    code: z
      .string()
      .trim()
      .min(1, 'Code is required')
      .max(100, 'Code must be 100 characters or fewer'),
    description: pricebookDescriptionSchema.optional(),
    type: z.enum(ITEM_TYPES, { error: 'Choose an item type' }),
    basePrice: z
      .number({ error: 'Base price must be a number' })
      .min(0, 'Base price cannot be negative')
      .default(0),
    laborRate: z
      .number({ error: 'Labor rate must be a number' })
      .min(0, 'Labor rate cannot be negative')
      .default(0),
    // SERVICE — what an hour COSTS us, and how many hours the work takes.
    // Cost only: neither ever moves the price. See SPEC-line-item-model,
    // "Flat vs Dynamic".
    laborCostRate: z
      .number({ error: 'Labor cost rate must be a number' })
      .min(0, 'Labor cost rate cannot be negative')
      .default(0),
    estimatedHours: z
      .number({ error: 'Hours must be a number' })
      .min(0, 'Hours cannot be negative')
      .default(0),
    laborCostLines: z
      .array(
        z.object({
          id: z.string().optional(),
          label: z.string().default(''),
          durationMinutes: z.number().min(0).default(0),
          rateSource: z.enum(['FIXED', 'PROFILE']).default('FIXED'),
          fixedRate: z.number().min(0).optional(),
          laborRateProfileId: z.string().optional(),
        }),
      )
      .default([]),
    costBasis: z
      .number({ error: 'Cost must be a number' })
      .min(0, 'Cost cannot be negative')
      .default(0),
    markupPercent: z
      .number({ error: 'Markup must be a number' })
      .min(-100, 'Markup cannot be lower than -100%')
      .default(0),
    // NOT-605 member pricing (per-item override). null = fall through to the
    // category / tenant default.
    memberPrice: z
      .number({ error: 'Member price must be a number' })
      .min(0, 'Member price cannot be negative')
      .nullable()
      .default(null),
    memberDiscountPercent: z
      .number({ error: 'Member discount must be a number' })
      .min(0, 'Member discount cannot be negative')
      .max(100, 'Member discount cannot exceed 100%')
      .nullable()
      .default(null),
    currency: z.string().max(3, 'Use a 3-letter currency code (e.g. USD)').default('USD'),
    priceStrategy: z.enum(['FLAT', 'DYNAMIC', 'COMPONENT_SUM']).default('FLAT'),
    unit: z.string().optional(),
    taxable: z.boolean().default(true),
    taxCategory: z.string().optional(),
    taxCode: z.string().optional(),
    taxLineType: z.union([z.enum(TAX_LINE_TYPES), z.literal('')]).default(''),
    glAccount: z.string().optional(),
    glClass: z.string().optional(),
    glCogs: z.string().optional(),
    glExpenseAccount: z.string().optional(),
    displayOrder: z
      .number({ error: 'Display order must be a number' })
      .min(0, 'Display order cannot be negative')
      .default(0),
    hidePrice: z.boolean().default(false),
    // NOT-590 — how the customer sees a service's included parts. Presentation
    // only: the same lines and the same total either way.
    componentDisplayMode: z.enum(['ITEMIZED', 'ROLLED_UP']).default('ROLLED_UP'),
    // Which detail the single customer-facing line shows.
    lineDetail: z.enum(['AUTO', 'DESCRIPTION', 'DESCRIPTION_AND_AUTO']).default('AUTO'),
    requirePhotos: z.boolean().default(false),
    requireAsset: z.boolean().default(false),
    aiTags: z.array(z.string()).default([]),
    requiredAssets: z.array(z.string()).default([]),
    equipmentDescriptionId: z.string().optional().or(z.literal('')),
    // Equipment spec + warranty, captured on the item itself. The backend
    // still stores these on an EquipmentDescription; the form no longer makes
    // the operator build that separately and match the two up afterwards.
    equipmentCategoryKey: z.string().optional().or(z.literal('')),
    brand: z.string().max(120).optional().or(z.literal('')),
    equipmentModel: z.string().max(120).optional().or(z.literal('')),
    manufacturer: z.string().max(120).optional().or(z.literal('')),
    mpn: z.string().max(120).optional().or(z.literal('')),
    upc: z.string().max(120).optional().or(z.literal('')),
    lengthIn: z.number().nullable().default(null),
    widthIn: z.number().nullable().default(null),
    heightIn: z.number().nullable().default(null),
    weightLbs: z.number().nullable().default(null),
    capacity: z.string().max(120).optional().or(z.literal('')),
    fuelType: z.string().max(120).optional().or(z.literal('')),
    efficiencyRating: z.string().max(120).optional().or(z.literal('')),
    refrigerantType: z.string().max(120).optional().or(z.literal('')),
    filterSize: z.string().max(120).optional().or(z.literal('')),
    expectedLifespanYears: z.number().nullable().default(null),
    defaultManufacturerWarrantyMonths: z.number().nullable().default(null),
    defaultManufacturerWarrantyTerms: z.string().max(2000).optional().or(z.literal('')),
    defaultServiceProviderWarrantyMonths: z.number().nullable().default(null),
    defaultServiceProviderWarrantyTerms: z.string().max(2000).optional().or(z.literal('')),
    isConfigurable: z.boolean().default(false),
    configurations: z
      .array(
        z.object({
          name: z.string().trim().min(1, 'Configuration name is required').max(120),
          price: z.number().min(0).optional(),
          attributes: z.record(z.string(), z.unknown()).optional(),
        }),
      )
      .default([]),
    hazardLevel: z
      .number({ error: 'Hazard level must be a number' })
      .min(0, 'Hazard level must be between 0 and 5')
      .max(5, 'Hazard level must be between 0 and 5')
      .default(0),
    retrievalBoost: z
      .number({ error: 'Retrieval boost must be a number' })
      .min(0, 'Retrieval boost cannot be negative')
      .default(1),
    // Membership
    membershipPlanId: z.string().optional(),
    membershipGrantTierId: z.string().optional(),
    membershipPlanIds: z.array(z.string()).default([]),
  })
  .refine(
    (val) => !(val.type === 'equipment' && val.isConfigurable) || val.configurations.length > 0,
    {
      path: ['configurations'],
      message: 'Add at least one configuration for a configurable equipment item.',
    },
  )
  // The chosen configuration's NAME is what identifies a configured line (see
  // lineIdentityKey and the server's assertConfigurableLinesResolved), so two
  // configurations sharing a name would be indistinguishable once sold.
  .refine(
    (val) => {
      if (val.type !== 'equipment' || !val.isConfigurable) return true;
      const names = val.configurations.map((c) => c.name.trim().toLowerCase());
      return new Set(names).size === names.length;
    },
    {
      path: ['configurations'],
      message: 'Configuration names must be unique.',
    },
  )
  // An equipment item still links an equipment type — but the operator no
  // longer has to have built one first. The spec panel on this form IS the
  // type, created and linked on save, so the only thing to insist on is the
  // one field the type cannot be created without: its category. (Asked here
  // rather than left to the server, which rejects an unknown or not-enabled
  // category with a 400 after the fact.)
  //
  // Asked through the shared `equipmentCategoryRequirement` so the sidebar
  // checklist cannot say anything different from what this refine enforces.
  .refine(
    (val) =>
      equipmentCategoryRequirement({
        isEquipment: val.type === 'equipment',
        equipmentDescriptionId: val.equipmentDescriptionId,
        equipmentCategoryKey: val.equipmentCategoryKey,
        // A new equipment item's type is written from this form every time, so
        // there is no untouched-panels case to grandfather here.
        writesEquipmentType: true,
      }) !== 'missing',
    {
      path: ['equipmentCategoryKey'],
      message: 'Choose an equipment category — the equipment type is created from it.',
    },
  );

type ItemFormValues = z.input<typeof itemSchema>;

/** Human-readable labels for surfacing field-level validation errors (NOT-49). */
const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  code: 'Code',
  description: 'Description',
  type: 'Type',
  basePrice: 'Base price',
  laborRate: 'Labor rate',
  laborCostRate: 'Labor cost rate',
  estimatedHours: 'Hours',
  costBasis: 'Cost',
  markupPercent: 'Markup',
  currency: 'Currency',
  unit: 'Unit',
  taxCategory: 'Tax category',
  taxCode: 'Tax code',
  taxLineType: 'Tax line type',
  glAccount: 'GL account',
  glClass: 'GL class',
  glCogs: 'GL COGS',
  glExpenseAccount: 'GL expense account',
  displayOrder: 'Display order',
  hazardLevel: 'Hazard level',
  retrievalBoost: 'Retrieval boost',
  equipmentDescriptionId: 'Equipment description',
  equipmentCategoryKey: 'Equipment category',
  configurations: 'Configurations',
  membershipPlanId: 'Membership plan',
};

/** Flatten RHF field errors into `Label: message` strings for a toast. */
// eslint-disable-next-line react-refresh/only-export-components
export function collectFieldIssues(errors: FieldErrors<ItemFormValues>): string[] {
  return Object.entries(errors)
    .map(([field, err]) => {
      const message = (err as { message?: unknown } | undefined)?.message;
      if (typeof message !== 'string' || !message) return null;
      return `${FIELD_LABELS[field] ?? field}: ${message}`;
    })
    .filter((issue): issue is string => issue !== null);
}

/**
 * What a brand-new item's pricing config starts as.
 *
 * A function, not a shared constant: the config is mutated in place by the
 * editors below, so handing every reset the same object would let one item's
 * edits reach the next through the default itself.
 */
function blankDynamicPricingConfig(): DynamicServicePricingConfig {
  return {
    enabled: true,
    useSameMarkupProfile: true,
    laborLines: [],
  };
}

function nameToCode(name: string, type: ItemType): string {
  const prefixMap: Record<ItemType, string> = {
    service: 'SVC',
    material: 'MAT',
    equipment: 'EQP',
    bundle: 'BND',
    membership: 'MEM',
    fee: 'FEE',
  };
  const slug = name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 20);
  return slug ? `${prefixMap[type]}-${slug}` : '';
}

function toOptionalString(value?: string): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

const ITEM_FORM_DEFAULTS: ItemFormValues = {
  name: '',
  code: '',
  description: '',
  type: 'service',
  basePrice: 0,
  laborRate: 0,
  laborCostRate: 0,
  estimatedHours: 0,
  laborCostLines: [],
  costBasis: 0,
  markupPercent: 0,
  memberPrice: null,
  memberDiscountPercent: null,
  currency: 'USD',
  priceStrategy: 'FLAT',
  unit: 'EA',
  taxable: true,
  taxCategory: 'DEFAULT',
  taxCode: '',
  taxLineType: '',
  glAccount: '',
  glClass: '',
  glCogs: '',
  glExpenseAccount: '',
  displayOrder: 0,
  hidePrice: false,
  lineDetail: 'AUTO' as const,
  componentDisplayMode: 'ROLLED_UP' as const,
  requirePhotos: false,
  requireAsset: false,
  aiTags: [],
  requiredAssets: [],
  equipmentDescriptionId: '',
  isConfigurable: false,
  configurations: [],
  hazardLevel: 0,
  retrievalBoost: 1,
  membershipPlanId: '',
  membershipGrantTierId: '',
  membershipPlanIds: [],
};

/**
 * The folders an item may still be filed in after its TYPE changes.
 *
 * A folder belongs to one item family, and the picker only ever offers its own
 * family's folders. Duplicating a MATERIAL and switching the copy to SERVICE
 * therefore strands the seeded material-folder ids: the picker no longer lists
 * them, but the save path posts `categoryIds` wholesale and the server files by
 * id — `assertCategoriesExist` checks existence and nothing else, deliberately
 * ("a folder accepts any item in the tenant's catalog — no type check, by
 * design"). So the new service lands in material folders, and there is no 400
 * to tell anyone: it is silent bad data.
 *
 * FILTER, never clear. A folder with no family at all is a legacy any-kind
 * folder that both families legitimately offer, and clearing outright would
 * throw away a correct assignment. `offeredIds` is exactly what the new
 * family's list returned, so any-kind folders survive by being in it.
 *
 * Order is preserved, so the primary stays the primary — and if the primary was
 * the folder dropped, the next one inherits it, the same promotion rule the
 * picker and `ensurePrimary` both apply.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function categoryIdsStillOffered(
  categoryIds: readonly string[],
  offeredIds: ReadonlySet<string>,
): string[] {
  return categoryIds.filter((id) => offeredIds.has(id));
}

/**
 * Why Save is briefly unavailable after the type changes, or after a folder is
 * created from the picker: the item's folders are being re-checked against the
 * family it is now in.
 */
const CHECKING_FOLDERS_HINT = 'Checking this item’s folders…';

/**
 * What a look at the folder list actually told us.
 *
 * A React Query `refetch()` RESOLVES when the request FAILS, carrying the last
 * successful `data` with it. So "came back populated" is not the same fact as
 * "came back current": an errored look that still holds the pre-create list
 * would otherwise be accepted as a fresh answer and drop the folder the
 * operator just made. Only a look that actually succeeded counts as a look.
 *
 * This is the same trap as `isSuccess` being true mid-refetch, one level down —
 * a resolved promise is not a successful request.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function offeredIdsFromRefetch(result: {
  isSuccess: boolean;
  data?: readonly { id: string }[];
}): ReadonlySet<string> | null {
  if (!result.isSuccess || !result.data) return null;
  return new Set(result.data.map((category) => category.id));
}

/**
 * The folders to POST, decided at the moment of the write.
 *
 * THE RULE, and there is only one: **a selected id the offered set does not
 * contain is the proof that the set is not current.** Not a fetch flag, not a
 * timestamp, not "which mutation just resolved" — the contradiction itself.
 *
 * That replaces three separate timing conditions, each of which was added
 * after a different window was found where "the set is current" was assumed
 * and wasn't: the type changed and the new family's list had not arrived; a
 * folder was created here and React Query was still serving the pre-create
 * list; a folder was created here and the invalidation had not yet even
 * started the refetch, so the query was neither fetching nor current. Every
 * one of those windows has the same observable symptom — an id in hand that
 * the set cannot explain — so observing the symptom covers windows nobody has
 * thought of yet, which enumerating moments never will.
 *
 * Then, exactly once:
 *
 * - The set explains every selected id -> it is current enough to decide with,
 *   whatever any flag says, and nothing needs dropping. No request at all.
 * - It does not -> go and look, ONCE, and decide on what comes back.
 * - Still missing after that look -> the folder is genuinely not offered: a
 *   different family (the bug this exists to stop) or deleted by someone else.
 *   Either way it is dropped, and there is no second lap. "Not current, look
 *   again" and "looked, still absent, it is gone" are different answers and
 *   only the first causes a fetch.
 * - The look itself failed -> `null`, and the caller REFUSES the save. Posting
 *   an unfiltered set is the very bug this exists to stop; guessing is not one
 *   of the options.
 *
 * An item filed in NO folder short-circuits before any of it: nothing to
 * explain, so a slow or broken folder list can never block saving an item that
 * does not use folders.
 */
// eslint-disable-next-line react-refresh/only-export-components
export async function categoryIdsForSave(
  categoryIds: readonly string[],
  offered: {
    /** What we currently believe this family offers; `null` if never told. */
    known: ReadonlySet<string> | null;
    /** Go and ask again. `null` when the request itself failed. */
    refetch: () => Promise<ReadonlySet<string> | null>;
  },
): Promise<string[] | null> {
  // This helper decides whether the currently offered folder set is fresh
  // enough to save. Preserve real UI ids here (tests and legacy tenants may
  // not use UUID-shaped identifiers); the API-specific UUID filter belongs at
  // the payload boundary after this asynchronous decision has completed.
  const ids = normalizeIdList(categoryIds);
  if (ids.length === 0) return [];

  // Every id accounted for: the set cannot be stale in any way that matters
  // here, because staleness could only ever hide an id we are holding.
  if (offered.known && ids.every((id) => offered.known!.has(id))) return ids;

  const fresh = await offered.refetch();
  if (!fresh) return null;
  return categoryIdsStillOffered(ids, fresh);
}

/** Seed the form from an existing item for "Duplicate" (mirror of estimates). */
// eslint-disable-next-line react-refresh/only-export-components
export function deriveItemFormValuesFromDuplicate(item: PricebookItem): ItemFormValues {
  const type = item.type === 'membership' ? 'service' : item.type;
  const isService = type === 'service';
  const isTimeAndMaterials = isService && item.priceStrategy === 'DYNAMIC';
  const isMaterial = type === 'material' || type === 'equipment';

  return {
    ...ITEM_FORM_DEFAULTS,
    name: `${item.name} (Copy)`,
    code: '', // unique — the user enters a new one
    description: item.description ?? '',
    // The new-item form does not create memberships → fall back to service.
    type,
    basePrice: isTimeAndMaterials ? 0 : (item.price ?? 0),
    laborRate: isTimeAndMaterials ? (item.laborRate ?? 0) : 0,
    // Cost, not price — copied for BOTH modes, for the same reason the edit
    // form keeps it: what an hour costs is true however the price is set.
    laborCostRate: item.laborCostRate ?? 0,
    estimatedHours: item.estimatedHours ?? 0,
    laborCostLines: item.laborCostLines ?? [],
    // Supplier rows are not duplicated, so a copied material/equipment has no
    // cost until the operator attaches a supplier to it.
    costBasis: isService && !isTimeAndMaterials ? (item.cost ?? 0) : 0,
    markupPercent: isMaterial ? (item.markupPercent ?? 0) : 0,
    memberPrice: item.memberPrice ?? null,
    memberDiscountPercent: item.memberDiscountPercent ?? null,
    currency: item.currency ?? 'USD',
    // A copy is priced the way the thing it copies is priced. Collapsing this
    // to FLAT turned every duplicated summed bundle into a flat one — the same
    // silent conversion the edit round-trip was fixed for, still live on this
    // path. It also made the gate below dead code for exactly the bundles it
    // exists to protect, since `isSummedBundle` could never be true here.
    priceStrategy: priceStrategyForItemType(type, item.priceStrategy),
    unit: isTimeAndMaterials ? 'HR' : (item.unit ?? 'EA'),
    taxable: item.taxable ?? true,
    taxCategory: item.taxCategory ?? 'DEFAULT',
    taxCode: item.taxCode ?? '',
    taxLineType: (item.taxLineType as ItemFormValues['taxLineType']) ?? '',
    glAccount: item.glAccount ?? '',
    glClass: item.glClass ?? '',
    glCogs: item.glCogs ?? '',
    glExpenseAccount: item.glExpenseAccount ?? '',
    displayOrder: item.displayOrder ?? 0,
    hidePrice: item.displayConfig?.hidePrice ?? false,
    // The copy should read like the original. An item that states nothing has
    // its description AND its parts printed by every renderer, so the copy
    // preselects the option that does that rather than 'AUTO', which prints
    // the parts alone.
    lineDetail: item.displayConfig?.lineDetail ?? 'DESCRIPTION_AND_AUTO',
    requirePhotos: item.displayConfig?.requirePhotos ?? false,
    requireAsset: item.displayConfig?.requireAsset ?? false,
    aiTags: item.aiTags ?? [],
    requiredAssets: item.requiredAssets ?? [],
    equipmentDescriptionId: item.equipmentDescriptionId ?? '',
    // A copy keeps the original's bundle treatment. The form default is
    // ROLLED_UP for a brand-new service, but duplicating a service that was
    // deliberately itemized must not quietly re-decide how it prints AND how it
    // is taxed — the parts come across, so the mode has to as well.
    componentDisplayMode:
      (item.componentDisplayMode as ItemFormValues['componentDisplayMode']) ??
      ITEM_FORM_DEFAULTS.componentDisplayMode,
    isConfigurable: type === 'equipment' ? (item.isConfigurable ?? false) : false,
    configurations: type === 'equipment' ? (item.configurations ?? []) : [],
    hazardLevel: Math.min(5, Math.max(0, item.hazardLevel ?? 0)),
    retrievalBoost: item.retrievalBoost ?? 1,
  };
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function NewPricebookItemPage() {
  const businessUnitsQuery = useBusinessUnits('active');
  const businessUnits = businessUnitsQuery.data ?? [];
  const hasNoBusinessUnits = businessUnitsQuery.isSuccess && businessUnits.length === 0;
  const {
    type: defaultType,
    duplicate,
    equipmentDescriptionId: prefillDescriptionId,
  } = Route.useSearch();
  const navigate = useNavigate();
  const createItem = useCreateItem();
  // The create already says "Item created"; pricing it is part of that, not a
  // second event to announce.
  const applyDynamicPrice = useApplyDynamicPrice({ notify: false });
  const updateItem = useUpdateItem();
  const uploadImage = useUploadPricebookImage();
  const dupQuery = usePricebookItem(duplicate ?? '');
  const didDuplicateRef = useRef(false);
  /**
   * Whether the member list can be believed when it is EMPTY.
   *
   * State rather than the ref beside it because the summed-bundle price effect
   * has to re-run the moment it flips — a ref changes nothing and the derived
   * price would sit stale until some other dependency happened to move.
   */
  const [duplicateSeeded, setDuplicateSeeded] = useState(false);
  // Prefill from a deep-linked equipment type ("Add to pricebook" on the types
  // list): preselect the link and mirror the type's name/code once.
  const prefillDescriptionQuery = useEquipmentDescription(
    prefillDescriptionId,
    !duplicate && !!prefillDescriptionId,
  );
  const didPrefillDescriptionRef = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const scrollBodyRef = useRef<HTMLDivElement>(null);

  // State for complex nested objects (outside zod schema)
  const [components, setComponents] = useState<ServiceComponent[]>([]);
  const [dynamicPricingConfig, setDynamicPricingConfig] =
    useState<DynamicServicePricingConfig>(blankDynamicPricingConfig);
  const [componentItemCache, setComponentItemCache] = useState<PricebookComponentItemCache>({});
  const [upsellItems, setUpsellItems] = useState<UpsellItem[]>([]);
  const [tagIds, setTagIds] = useState<string[]>([]);
  // Browse folders. Order is meaningful — the first is the primary folder.
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const {
    pendingFile: pendingImageFile,
    preview: imagePreview,
    selectImage,
    clearImage,
    setRemotePreview,
  } = useImagePreview();
  const [basicInfoOpen, setBasicInfoOpen] = useState(true);
  const [componentsSectionOpen, setComponentsSectionOpen] = useState(true);
  const [membershipLinkOpen, setMembershipLinkOpen] = useState(true);
  const [taxOpen, setTaxOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [displayOpen, setDisplayOpen] = useState(false);

  const form = useForm<ItemFormValues>({
    resolver: zodResolver(itemSchema),
    defaultValues: { ...ITEM_FORM_DEFAULTS, type: defaultType },
  });

  const isMutating = createItem.isPending || uploadImage.isPending || updateItem.isPending;
  // A half-filled item is real work: leaving the page — Back, Cancel, the tab —
  // must ask first. A save in flight is exempt; it is the reason we are leaving.
  const unsavedChanges = useUnsavedChangesGuard({
    when: form.formState.isDirty && !isMutating,
  });

  const watchedType = form.watch('type');

  // Changing the type changes which family's folders the picker offers, so any
  // folder the new family does not offer has to go — see
  // `categoryIdsStillOffered` for why this filters instead of clearing, and for
  // the silent bad data it prevents.
  const categoryFamily = categoryFamilyForItemType(watchedType);
  const offeredCategories = useCategoryList({ itemFamily: categoryFamily });
  // Whatever we have been told so far — no judgement about how current it is.
  // `categoryIdsForSave` decides that from the ids themselves at write time,
  // which is the one place it can be decided without guessing.
  const offeredCategoryIds = useMemo(
    () => (offeredCategories.data ? new Set(offeredCategories.data.map((c) => c.id)) : null),
    [offeredCategories.data],
  );

  /**
   * Go and ask again, and report what came back. `null` when the request
   * itself failed, which `categoryIdsForSave` turns into a refused save rather
   * than an unfiltered write.
   */
  async function refetchOfferedCategoryIds(): Promise<ReadonlySet<string> | null> {
    return offeredIdsFromRefetch(await offeredCategories.refetch());
  }

  // COSMETIC ONLY — the chips follow the type so the operator sees the folders
  // change rather than discovering it in the payload. Correctness does not
  // depend on this having run, or having run against a current list: the write
  // re-decides from the ids themselves. Guarded on a settled list purely so it
  // does not blink a folder away and put it back.
  const filedUnderFamilyRef = useRef(categoryFamily);
  const offeredCategoriesSettled = offeredCategories.isSuccess && !offeredCategories.isFetching;
  useEffect(() => {
    if (filedUnderFamilyRef.current === categoryFamily) return;
    if (!offeredCategoriesSettled || !offeredCategoryIds) return;
    filedUnderFamilyRef.current = categoryFamily;
    setCategoryIds((ids) => categoryIdsStillOffered(ids, offeredCategoryIds));
  }, [categoryFamily, offeredCategoriesSettled, offeredCategoryIds]);

  // Also cosmetic: while a folder request is actually in flight, Save says so
  // rather than taking a click it would only have to wait on. Bounded by the
  // request, so it can never stick — in particular it does NOT key off "an id
  // the list does not explain", which a folder someone else deleted would
  // satisfy forever.
  const checkingCategories = categoryIds.length > 0 && offeredCategories.isFetching;
  const watchedTaxable = form.watch('taxable');
  const watchedName = form.watch('name');
  const watchedCode = form.watch('code');
  const watchedBasePrice = form.watch('basePrice');
  const watchedLaborRate = form.watch('laborRate');
  const watchedPriceStrategy = form.watch('priceStrategy');
  const watchedDescription = form.watch('description');
  const watchedMembershipPlanId = form.watch('membershipPlanId');
  const watchedEquipmentCategoryKey = form.watch('equipmentCategoryKey');
  const watchedEquipmentDescriptionId = form.watch('equipmentDescriptionId');
  const lastGeneratedCodeRef = useRef(nameToCode('', defaultType));
  const userHasEditedCodeRef = useRef(false);
  // How the service's price and its parts combine is the pricing strategy. A
  // time-and-materials service lists an hourly rate that parts do not add to; a
  // flat-price service sells for the price typed here, with the parts as cost.
  const isTimeAndMaterials = isTimeAndMaterialsPricebookItem({
    type: watchedType,
    priceStrategy: watchedPriceStrategy,
  });
  const isFlatPriceService = isFlatPricePricebookService({
    type: watchedType,
    priceStrategy: watchedPriceStrategy,
  });
  const isBundle = watchedType === 'bundle';
  /** A bundle priced from its members rather than by a figure the operator typed. */
  const isSummedBundle = isBundle && watchedPriceStrategy === 'COMPONENT_SUM';
  // See the edit route: a bundle's own price is the whole of what it charges,
  // and stating it here rather than zeroing it is what stops the members being
  // charged a second time on top of the price that already contains them.
  const bundleServicePrice = isTimeAndMaterials ? 0 : Number(watchedBasePrice || 0);
  // The pricing defaults go in because a part row that names no profile of its
  // own falls back to them — without that the summary priced every part at its
  // catalog price and then reported a markup the operator had declined.
  const componentTotals = useComponentTotals(
    components,
    componentItemCache,
    bundleServicePrice,
    dynamicPricingConfig,
    isFlatPriceService,
    isBundle,
  );

  // A duplicate seeds its members from the source item asynchronously; every
  // other way onto this route starts with a list only the operator fills.
  const membersAreSettled = !duplicate || duplicateSeeded;

  // See the edit route: "Add up what is in the bundle" means the price
  // follows the contents, and the operator has to see it do so.
  useEffect(() => {
    if (!isSummedBundle) return;
    // EMPTY MEANS EMPTY, and the price has to follow the contents all the way
    // down to zero. Refusing to write when there are no members left the
    // removal of the LAST one showing the price of a bundle that no longer
    // contains it — a $400 member added and then deleted kept charging $400.
    //
    // What that guard was really protecting against is emptiness that means
    // "not loaded yet". On this route there is nothing saved to protect: the
    // list starts empty and only the operator fills it. The one exception is
    // the duplicate seed, which arrives async — until it lands, empty is
    // ambiguous here too.
    if (!membersAreSettled) return;
    const derived = summedBundlePrice(componentTotals);
    if (derived == null) return;
    if (Number(form.getValues('basePrice') ?? 0) === derived) return;
    form.setValue('basePrice', derived, { shouldDirty: true, shouldValidate: false });
  }, [form, isSummedBundle, componentTotals, membersAreSettled]);
  // PREVIEW ONLY. After-hours and the add-on discount are conditional on the
  // JOB, so the catalog price excludes them — which left an operator able to
  // configure either and unable to see what it does short of selling one.
  // Nothing here is saved; the everyday price above is untouched.
  const [previewAfterHours, setPreviewAfterHours] = useState(false);
  const [previewAsAddOn, setPreviewAsAddOn] = useState(false);
  const dynamicSummary = useDynamicServiceSummary(
    dynamicPricingConfig,
    components,
    componentItemCache,
  );
  const adjustmentPreview = previewOptionalAdjustments({
    price: dynamicSummary.calculatedPrice,
    config: dynamicPricingConfig,
    afterHours: previewAfterHours,
    asAddOn: previewAsAddOn,
  });
  const showBundleTotal = components.length > 0 && !isTimeAndMaterials;
  // The labor the operator entered in "Labor used by this service", reduced the
  // same way the estimate reduces it — one function, so the catalog and the
  // document it becomes cannot report different costs for the same service.
  const { data: laborRateProfiles = [] } = useLaborRateProfiles();
  // Through the ONE reconstruction, the same function the labor editor above
  // uses. Reading `laborCostLines` raw printed Labor $0.00 for a service saved
  // before the rows existed — while the editor on the same screen showed the row
  // it rebuilt from `estimatedHours` / `laborCostRate`.
  const flatLaborLines = flatServiceLaborLines({
    laborCostLines: form.watch('laborCostLines') as ServiceLaborLine[] | undefined,
    estimatedHours: form.watch('estimatedHours') as number | undefined,
    laborCostRate: form.watch('laborCostRate') as number | undefined,
  });
  const flatServiceCost = flatServiceCostBreakdown(
    componentTotals,
    flatLaborLines,
    laborRateProfiles,
    Number(watchedBasePrice) || 0,
  );

  // A bundle exists to hold its members, so it always carries the section.
  const hasComponents = watchedType === 'service' || isBundle;
  // A new item has no id yet, so it can have no suppliers and its cost stays
  // editable until it is saved.
  // A new item has no id yet, so suppliers are collected here and attached the
  // moment it exists — a material is created complete rather than created and
  // then edited.
  const [draftSuppliers, setDraftSuppliers] = useState<DraftItemSupplier[]>([]);
  const attachSupplier = useAttachSupplier();
  const createEquipmentDescription = useCreateEquipmentDescription();
  const updateEquipmentDescription = useUpdateEquipmentDescription();
  // A type this form has already created, kept so a failed item create can be
  // retried without stranding — or colliding with — the type it left behind.
  const createdEquipmentTypeRef = useRef<string | undefined>(undefined);
  const primaryDraftSupplier = draftSuppliers.find((row) => row.isPrimary);
  const primarySupplierName = primaryDraftSupplier?.supplierName;
  const isMembership = watchedType === 'membership';
  const isService = watchedType === 'service';
  const isMaterial = watchedType === 'material' || watchedType === 'equipment';
  // The cost field MIRRORS the primary supplier here exactly as it does on the
  // edit route — the only difference is where the primary lives. This form has
  // no item yet, so its suppliers are drafts in local state rather than rows
  // the API can be asked about, and reading only the API meant the mirror never
  // moved: the header quoted "Cost $0.00 from easy" beside a table plainly
  // showing $20.00, and Markup sat disabled on "unavailable when Unit Cost is
  // $0.00" — unreachable for the whole of item creation, which is the one time
  // a material's markup is most likely to be set.
  //
  // Keyed on the primary's cost, so promoting the other supplier moves the cost
  // with it. Without a primary supplier the effective cost is zero: materials
  // and equipment no longer have a separately authored cost.
  const primaryDraftSupplierCost = primaryDraftSupplier
    ? supplierStockCost(primaryDraftSupplier)
    : undefined;
  useEffect(() => {
    const current = Number(form.getValues('costBasis') ?? 0);
    const next = costBasisFromPrimarySupplier({
      isMaterial,
      primaryCost: primaryDraftSupplierCost,
      currentCostBasis: current,
    });
    if (next === null) return;
    // Dirty, unlike the edit route: there it mirrors what the server already
    // stores, whereas here it is the operator's own unsaved input.
    form.setValue('costBasis', next, { shouldDirty: true, shouldValidate: false });
  }, [form, isMaterial, primaryDraftSupplierCost]);

  // And the price has to FOLLOW what the mirror writes — see
  // `pricingAfterMirroredCost` for the rule and the −100% markup it closes.
  // Keyed on the mirrored field itself rather than the supplier, so it also
  // covers the cost being handed back to a duplicate's seeded figure.
  const watchedCostBasis = form.watch('costBasis');
  // Survives a type detour on purpose: the price VALUE survives one too
  // (`handleTypeChange` leaves basePrice alone), so its provenance has to
  // travel with it. Cleared on leaving material, a derived price would come
  // back from bundle-and-back reading as authored — standing still under the
  // next supplier change while markup absorbed it, which is the bug this rule
  // closes. Reset only where the price itself resets: `applyPerItemState`.
  const lastDerivedBasePriceRef = useRef<number | null>(null);
  useEffect(() => {
    const next = pricingAfterMirroredCost({
      isMaterial,
      costBasis: Number(watchedCostBasis ?? 0),
      currentBasePrice: Number(form.getValues('basePrice') ?? 0),
      currentMarkupPercent: Number(form.getValues('markupPercent') ?? 0),
      lastDerivedBasePrice: lastDerivedBasePriceRef.current,
    });
    if (next === null) return;
    if ('basePrice' in next) {
      // Writing 0 is the reversal: the chain ends rather than starts over.
      lastDerivedBasePriceRef.current = next.basePrice === 0 ? null : next.basePrice;
      form.setValue('basePrice', next.basePrice, { shouldDirty: true, shouldValidate: false });
    } else {
      form.setValue('markupPercent', next.markupPercent, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }, [form, isMaterial, watchedCostBasis]);

  /**
   * Everything on this form that belongs to ONE item and lives outside
   * `form` — the collections react-hook-form's own `reset` cannot reach.
   *
   * Stated as one whole object on purpose. This form is reset in two places
   * (after "Save & add another", and when the duplicate seed lands on a form
   * the operator may already have typed into), and both have to account for
   * every one of these. They did not: suppliers were left out of both, so the
   * previous item's suppliers were attached to the next one and its cost was
   * mirrored onto it. A required field per collection makes the next omission
   * a type error rather than a bug found in QA.
   *
   * Section expansion is deliberately NOT here — it describes what is unfolded
   * on screen, not what the item is, and the duplicate path sets it from the
   * source item's own contents.
   */
  const applyPerItemState = useCallback(
    (next: {
      components: ServiceComponent[];
      componentItemCache: PricebookComponentItemCache;
      /** Persisted on bundles and dynamic services, so a leak is a wrong SAVE. */
      dynamicPricingConfig: DynamicServicePricingConfig;
      upsellItems: UpsellItem[];
      tagIds: string[];
      categoryIds: string[];
      draftSuppliers: DraftItemSupplier[];
      /** Saved image to show. Any pending upload is discarded either way. */
      remoteImageUrl: string | null;
      /** An equipment type this form created, kept so a retry can reuse it. */
      createdEquipmentTypeId: string | undefined;
    }) => {
      setComponents(next.components);
      setComponentItemCache(next.componentItemCache);
      setDynamicPricingConfig(next.dynamicPricingConfig);
      setUpsellItems(next.upsellItems);
      setTagIds(next.tagIds);
      setCategoryIds(next.categoryIds);
      setDraftSuppliers(next.draftSuppliers);
      // Same for the price the mirror's follower derived: the next item's
      // untouched $0 must read as unauthored, not as this one's figure.
      lastDerivedBasePriceRef.current = null;
      // Always cleared first: a file chosen but not yet uploaded belongs to the
      // item being replaced, and on the duplicate path it could otherwise
      // outlive the form it was picked in.
      clearImage();
      if (next.remoteImageUrl) setRemotePreview(next.remoteImageUrl);
      createdEquipmentTypeRef.current = next.createdEquipmentTypeId;
    },
    [clearImage, setRemotePreview],
  );

  const isCodeDirty = Boolean(form.formState.dirtyFields.code);

  const handlePriceStrategyChange = useCallback(
    (value: PriceStrategy) => {
      if (value === 'FLAT') {
        form.setValue('laborRate', 0, {
          shouldDirty: true,
          shouldValidate: false,
        });
        return;
      }
      // Summing a bundle changes nothing about hours or unit — those belong to
      // the time-and-materials branch below.
      if (value === 'COMPONENT_SUM') return;
      // The typed flat price is LEFT ALONE. Switching to T&M used to wipe it,
      // so trying the other pricing type and coming back reported the service
      // as free — and the save path already sends 0 for a T&M service, so
      // clearing the field bought nothing.
      form.setValue('costBasis', 0, {
        shouldDirty: true,
        shouldValidate: false,
      });
      form.setValue('unit', 'HR', { shouldDirty: true, shouldValidate: false });
    },
    [form],
  );

  const handleTypeChange = useCallback(
    (nextType: SelectableItemType, opts?: { shouldDirty?: boolean }) => {
      const shouldDirty = opts?.shouldDirty ?? true;
      const currentPriceStrategy = form.getValues('priceStrategy');
      const nextPriceStrategy =
        nextType === 'service' && currentPriceStrategy === 'DYNAMIC' ? 'DYNAMIC' : 'FLAT';

      form.setValue('type', nextType, { shouldDirty });
      form.setValue('priceStrategy', nextPriceStrategy, {
        shouldDirty,
        shouldValidate: false,
      });

      if (nextPriceStrategy === 'FLAT') {
        form.setValue('laborRate', 0, { shouldDirty, shouldValidate: false });
      } else {
        // Price survives the switch — see `handlePriceStrategyChange`.
        form.setValue('costBasis', 0, { shouldDirty, shouldValidate: false });
        form.setValue('unit', 'HR', { shouldDirty, shouldValidate: false });
      }

      const allowsCost =
        nextType === 'material' ||
        nextType === 'equipment' ||
        (nextType === 'service' && nextPriceStrategy !== 'DYNAMIC');
      if (!allowsCost) {
        form.setValue('costBasis', 0, { shouldDirty, shouldValidate: false });
      }
      if (nextType !== 'material' && nextType !== 'equipment') {
        form.setValue('markupPercent', 0, {
          shouldDirty,
          shouldValidate: false,
        });
      }
    },
    [form],
  );

  useEffect(() => {
    if (duplicate) return; // duplicate seed owns the type
    handleTypeChange(defaultType, { shouldDirty: false });
  }, [defaultType, duplicate, handleTypeChange]);

  // Seed the form once from the source item when duplicating.
  useEffect(() => {
    if (!duplicate || !dupQuery.data || didDuplicateRef.current) return;
    didDuplicateRef.current = true;
    setDuplicateSeeded(true);
    const item = dupQuery.data;
    form.reset(deriveItemFormValuesFromDuplicate(item));
    // The seed lands asynchronously, on a form the operator could already have
    // typed into, so everything per-item is replaced — not just the fields the
    // source item has an opinion about. Suppliers are the case that proves it:
    // one attached in that window belongs to no item, would be saved onto the
    // duplicate, and would leave the cost mirror pointing at a baseline from a
    // form that no longer exists.
    applyPerItemState({
      components: item.components ?? [],
      // A lookup cache keyed by item id; it refills on demand.
      componentItemCache: {},
      // Taken from the source item, the same way the edit route hydrates it.
      // The duplicate of a bundle or a T&M service is priced the way the
      // original was — before this it kept the blank default, so duplicating
      // one quietly reset its markup profiles and labor lines.
      dynamicPricingConfig: item.dynamicPricingConfig ?? {},
      upsellItems: item.upsellItems ?? [],
      tagIds: sanitizeIdList((item.tags ?? []).map((t) => t.id)),
      // Primary first, matching how the picker reads the order back — the
      // duplicate opens on the folder the source item actually lives in, with
      // its other folders behind it.
      categoryIds: sanitizeIdList(
        [...(item.categories ?? [])]
          .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))
          .map((c) => c.categoryId),
      ),
      // The duplicate buys from whoever the NEW item will buy from; the source
      // item's suppliers are not copied.
      draftSuppliers: [],
      remoteImageUrl: item.imagePreviewUrl ?? null,
      createdEquipmentTypeId: undefined,
    });
    // NOT-36: mirror the edit form — auto-expand sections whose data carried
    // over from the duplicated source item so populated (e.g. AI Tags) sections
    // aren't mistaken for empty. Set explicitly (not open-if-data) so the state
    // reflects exactly the seeded item and never leaks a prior expansion. A
    // blank new item (no `duplicate`) never runs this effect, so its sections
    // stay collapsed.
    const sectionsWithData = computePricebookSectionOpenState(item);
    setAiOpen(sectionsWithData.aiOpen);
    setTaxOpen(sectionsWithData.taxOpen);
    setDisplayOpen(sectionsWithData.displayOpen);
    // `didDuplicateRef` guards the body, so the identity of `applyPerItemState`
    // cannot cause a re-seed even if its deps change.
  }, [duplicate, dupQuery.data, form, applyPerItemState]);

  // Seed the equipment link + name/code once from a deep-linked type.
  useEffect(() => {
    if (
      duplicate ||
      !prefillDescriptionId ||
      !prefillDescriptionQuery.data ||
      didPrefillDescriptionRef.current
    ) {
      return;
    }
    didPrefillDescriptionRef.current = true;
    const type = prefillDescriptionQuery.data;
    form.setValue('equipmentDescriptionId', type.id, {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (!form.getValues('name')?.trim()) {
      form.setValue('name', type.name, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    if (!form.getValues('code')?.trim()) {
      form.setValue('code', type.code, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [duplicate, prefillDescriptionId, prefillDescriptionQuery.data, form]);

  useEffect(() => {
    if (isCodeDirty) {
      userHasEditedCodeRef.current = true;
    }
  }, [isCodeDirty]);

  useEffect(() => {
    const nextGeneratedCode = nameToCode(watchedName, watchedType);
    const currentCode = watchedCode ?? '';

    // Detect user edit: the auto-fill always writes the value we tracked in
    // `lastGeneratedCodeRef`. If `currentCode` differs from that ref, the user
    // must have changed it directly (including clearing it). RHF's dirtyFields
    // can't detect this because a cleared field that matches the default value
    // ('') is not considered dirty.
    if (currentCode !== lastGeneratedCodeRef.current) {
      userHasEditedCodeRef.current = true;
    }

    if (userHasEditedCodeRef.current) {
      lastGeneratedCodeRef.current = nextGeneratedCode;
      return;
    }

    if (currentCode !== nextGeneratedCode) {
      form.setValue('code', nextGeneratedCode, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }

    lastGeneratedCodeRef.current = nextGeneratedCode;
  }, [watchedCode, watchedName, watchedType, form]);

  function goBack() {
    void navigate({ to: '/dashboard/pricebook', search: {} as never });
  }

  function navigateToCreatedItem(itemId: string | undefined) {
    // The item is saved, so this redirect must not be met with "you have
    // unsaved changes" — the form still reads dirty until it unmounts.
    unsavedChanges.allowNextNavigation();
    if (!itemId) {
      goBack();
      return;
    }

    void navigate({
      to: '/dashboard/pricebook/$id',
      params: { id: itemId },
      search: {} as never,
      replace: true,
    });
  }

  // NOT-49: surface exactly which fields are invalid instead of a silent/generic
  // failure. Use the errors RHF hands to onInvalid — `form.formState.errors` is
  // stale at this synchronous point.
  function handleInvalid(errors: FieldErrors<ItemFormValues>) {
    requestAnimationFrame(() => {
      const el = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      }
    });

    const issues = collectFieldIssues(errors);
    if (issues.length === 0) {
      toast.error('Please fix the highlighted fields before saving.');
      return;
    }
    toast.error(issues.length === 1 ? issues[0] : `Please fix ${issues.length} fields`, {
      description: issues.length === 1 ? undefined : issues.join(' • '),
    });
  }

  async function onSubmit(values: ItemFormValues, opts?: { addAnother?: boolean }) {
    try {
      const parsed = itemSchema.parse(values);

      // NOT-49: included parts are tracked outside the zod schema, so guard
      // them here (not by disabling the button) and surface a clear reason.

      // Every element of these is validated as a UUID server-side, so one blank
      // left over from a tag that could not be created would reject the whole
      // item — name, price, parts and all — over a selection the user cannot
      // even see. Drop the unusable entries; save what is real.
      const safeTagIds = sanitizeIdList(tagIds);
      // Filtered HERE, at the write, rather than trusting the pruning effect to
      // have run: the type can change and Save be clicked before the new
      // family's folder list has even arrived.
      const safeCategoryIds = await categoryIdsForSave(categoryIds, {
        known: offeredCategoryIds,
        refetch: refetchOfferedCategoryIds,
      });
      if (safeCategoryIds === null) {
        toast.error('Could not check this item’s folders. Try saving again in a moment.');
        return;
      }
      const categoryIdsForDto = sanitizeIdList(safeCategoryIds);

      // An equipment item and its make/model spec are born together, and the
      // spec is born FIRST: the server refuses an equipment item that links no
      // type, so the id has to travel out with the item rather than be stitched
      // on afterwards. The operator fills the spec panel on this form and never
      // has to go build the same equipment twice.
      let equipmentDescriptionId = parsed.equipmentDescriptionId?.trim() || undefined;
      if (parsed.type === 'equipment' && !equipmentDescriptionId) {
        try {
          equipmentDescriptionId = await ensureEquipmentType({
            carriedId: createdEquipmentTypeRef.current,
            spec: newEquipmentTypeSpec(parsed),
            create: (spec) => createEquipmentDescription.mutateAsync(spec as never),
            update: (id, spec) =>
              updateEquipmentDescription.mutateAsync({ id, input: spec as never }),
          });
          // Remembered BEFORE the item is created, so a failure there can be
          // retried against the type that already exists.
          createdEquipmentTypeRef.current = equipmentDescriptionId;
        } catch (error) {
          // Stop here. Carrying on would send the item without a link and the
          // server would reject it anyway, with a message about a type the
          // operator was never asked for.
          const detail = error instanceof Error ? error.message : 'unknown error';
          toast.error(`Could not save the equipment specification: ${detail}`);
          return;
        }
      }

      const dto: CreateItemDto = {
        name: parsed.name,
        code: parsed.code,
        type: parsed.type,
        status: 'ACTIVE',
        description: toOptionalString(parsed.description),
        basePrice: isService && parsed.priceStrategy === 'DYNAMIC' ? 0 : parsed.basePrice,
        laborRate: isService
          ? parsed.priceStrategy === 'DYNAMIC'
            ? parsed.laborRate || undefined
            : 0
          : undefined,
        // Cost only, and sent for a service in either mode.
        laborCostRate: isService ? parsed.laborCostRate || undefined : undefined,
        estimatedHours: isService ? parsed.estimatedHours || undefined : undefined,
        laborCostLines: isService ? parsed.laborCostLines : undefined,
        // A material/equipment cost is established by attaching its primary
        // supplier below. The item create itself never authors an orphan cost.
        costBasis:
          isService && parsed.priceStrategy !== 'DYNAMIC'
            ? parsed.costBasis || undefined
            : undefined,
        markupPercent: isMaterial ? parsed.markupPercent || undefined : undefined,
        // NOT-605 member pricing (per-item override)
        memberPrice: parsed.memberPrice,
        memberDiscountPercent: parsed.memberDiscountPercent,
        currency: parsed.currency?.toUpperCase() || undefined,
        // A bundle is priced either from its members (COMPONENT_SUM) or by a
        // typed price (FLAT); only a service uses DYNAMIC.
        priceStrategy: priceStrategyForItemType(parsed.type, parsed.priceStrategy),
        dynamicPricingConfig: isBundle
          ? dynamicPricingConfig
          : isService && parsed.priceStrategy === 'DYNAMIC'
            ? dynamicPricingConfig
            : { enabled: false },
        unit:
          isService && parsed.priceStrategy === 'DYNAMIC' ? 'HR' : toOptionalString(parsed.unit),
        taxable: parsed.taxable,
        taxCategory: toOptionalString(parsed.taxCategory),
        taxCode: toOptionalString(parsed.taxCode),
        // Only a class a reader will honour is worth storing. A derived
        // `labor`/`materials`/`flat_rate` is shown as "Auto", so sending it
        // back would persist an override the form just told the operator was
        // not there — and on a duplicate whose TYPE was changed it would store
        // a class that disagrees with what the new item is. Omitted, the server
        // derives the same answer it would have derived anyway.
        taxLineType: parsed.taxable === false ? 'exempt' : storableTaxLineType(parsed.taxLineType),
        glAccount: toOptionalString(parsed.glAccount),
        glClass: toOptionalString(parsed.glClass),
        glCogs: toOptionalString(parsed.glCogs),
        glExpenseAccount: toOptionalString(parsed.glExpenseAccount),
        displayOrder: parsed.displayOrder || undefined,
        displayConfig: {
          hidePrice: parsed.hidePrice,
          requirePhotos: parsed.requirePhotos,
          requireAsset: parsed.requireAsset,
          lineDetail: parsed.lineDetail,
        },
        aiTags: parsed.aiTags?.length ? parsed.aiTags : undefined,
        requiredAssets: parsed.requiredAssets?.length ? parsed.requiredAssets : undefined,
        equipmentDescriptionId: parsed.type === 'equipment' ? equipmentDescriptionId : undefined,
        isConfigurable: parsed.type === 'equipment' ? parsed.isConfigurable : false,
        configurations:
          parsed.type === 'equipment' && parsed.isConfigurable ? parsed.configurations : [],
        hazardLevel: parsed.hazardLevel,
        retrievalBoost: parsed.retrievalBoost !== 1 ? parsed.retrievalBoost : undefined,
        tagIds: safeTagIds.length ? safeTagIds : undefined,
        // Every folder the item was filed into, primary first.
        categoryIds: categoryIdsForDto.length ? categoryIdsForDto : undefined,
        // The picker keeps the primary first, so the server's "default to the
        // first" is exactly what the user sees marked Primary.
        primaryCategoryId: categoryIdsForDto[0],
      };

      // NOT-590 — always send the list on a SERVICE, so clearing the last part
      // actually clears it instead of silently leaving the old one attached.
      if (hasComponents) {
        // Built field by field rather than spread: the API forbids keys it does
        // not declare, and an out-of-union value fails the whole save.
        dto.components = toServiceComponentPayloads(components, !isService);
        dto.componentDisplayMode = isService
          ? 'ROLLED_UP'
          : (parsed.componentDisplayMode ?? 'ROLLED_UP');
      }
      if (isMembership) {
        if (parsed.membershipPlanId) {
          dto.membershipConfig = {
            planId: parsed.membershipPlanId,
            grantTierId: toOptionalString(parsed.membershipGrantTierId) ?? null,
          };
        }
        if (parsed.membershipPlanIds?.length) dto.membershipPlanIds = parsed.membershipPlanIds;
      }
      if (upsellItems.length > 0) {
        dto.upsellItems = upsellItems.filter((u) => u.itemId);
      }

      const result = await createItem.mutateAsync(dto);
      const itemId = (result as Record<string, unknown>)?.id as string | undefined;

      // MATERIALIZE a dynamic service the moment it exists.
      //
      // Its price is derived, and nothing derives it at create: the item is
      // written with `basePrice: 0` and only the "apply" action ever fills it
      // in. So a service the operator had just finished configuring was stored
      // as costing nothing — it listed at $0.00 in every picker, landed on
      // estimates at $0.00, and reported itself out of date the instant it was
      // created. The components are already saved with the item above, so the
      // calculation here sees the finished service.
      //
      // Best-effort: the item exists and is correct apart from this figure, so
      // a failure here is worth a warning, not a rolled-back create.
      if (itemId && parsed.type === 'service' && parsed.priceStrategy === 'DYNAMIC') {
        try {
          await applyDynamicPrice.mutateAsync({ serviceId: itemId, initial: true });
        } catch {
          toast.warning(
            'Item created, but its price could not be calculated. Open it and choose Update price.',
          );
        }
      }

      // Attach the suppliers collected while the item did not exist yet. In
      // order, so the primary is applied last and wins the cost sync.
      if (itemId && draftSuppliers.length > 0) {
        const ordered = [
          ...draftSuppliers.filter((row) => !row.isPrimary),
          ...draftSuppliers.filter((row) => row.isPrimary),
        ];
        for (const supplier of ordered) {
          try {
            await attachSupplier.mutateAsync({
              itemId,
              supplierId: supplier.supplierId,
              cost: supplier.cost,
              purchaseUnit: supplier.purchaseUnit,
              unitsPerPurchaseUnit: supplier.unitsPerPurchaseUnit,
              supplierPartNumber: supplier.supplierPartNumber ?? null,
              isPrimary: supplier.isPrimary,
            });
          } catch {
            // The item exists; say which supplier did not make it rather than
            // failing the whole save.
            toast.warning(`Could not attach ${supplier.supplierName}.`);
          }
        }
      }

      if (pendingImageFile && itemId) {
        try {
          const fileKey = await uploadImage.mutateAsync({
            id: itemId,
            file: pendingImageFile,
          });
          await updateItem.mutateAsync({ id: itemId, imageUrl: fileKey });
        } catch (error) {
          // NOT-53: surface the underlying upload error (S3 status + body,
          // or backend 4xx) so the next bug report includes the cause.
          // Generic "image upload failed" was hiding signature mismatches,
          // disallowed MIME types, and bucket-policy rejections.
          const detail = error instanceof Error ? error.message : 'unknown error';
          toast.warning(
            `Item created, but the image upload failed: ${detail}. You can re-upload it from the item page.`,
          );
        }
      }

      if (opts?.addAnother) {
        toast.success('Item created — add another');
        form.reset({
          ...ITEM_FORM_DEFAULTS,
          type: values.type,
        });
        // Empty on every count: the next item shares nothing with the one just
        // saved — not its parts, not its suppliers, not its image, and not the
        // equipment type this form created for it.
        applyPerItemState({
          components: [],
          componentItemCache: {},
          // Persisted on bundles and dynamic services: keeping the previous
          // item's markup profiles and labor lines would SAVE them onto the
          // next one, not merely show them.
          dynamicPricingConfig: blankDynamicPricingConfig(),
          upsellItems: [],
          tagIds: [],
          categoryIds: [],
          draftSuppliers: [],
          remoteImageUrl: null,
          createdEquipmentTypeId: undefined,
        });
        // The page itself never scrolls (FormPageLayout is `noscroll`) — the
        // form body is its own overflow container, so `window.scrollTo` was a
        // no-op and left the user parked at the bottom of a freshly emptied
        // form, looking at blank space instead of the Name field.
        scrollBodyRef.current?.scrollTo({ top: 0 });
        return;
      }

      navigateToCreatedItem(itemId);
    } catch (error) {
      if (isApiError(error)) {
        if (error.fieldErrors) applyServerErrorsToForm(form, error.fieldErrors);
        return;
      }
      throw error;
    }
  }

  // NOT-49: the submit buttons stay enabled while the form is invalid so a click
  // reaches `handleInvalid` and surfaces field-specific errors — a disabled
  // button would leave the user with no explanation. They only block while a
  // mutation is in flight (`isMutating`, above).
  const hasOptionalDetails =
    Boolean(watchedDescription?.trim()) ||
    tagIds.length > 0 ||
    categoryIds.length > 0 ||
    Boolean(imagePreview);
  const priceSummaryItem = {
    type: watchedType,
    price: Number(watchedBasePrice || 0),
    laborRate: Number(watchedLaborRate || 0),
    priceStrategy: watchedPriceStrategy,
  };
  const listedPrice = pricebookPrimaryPrice(priceSummaryItem);
  const listedPriceLabel = pricebookPrimaryPriceLabel(priceSummaryItem);
  const listedPriceSuffix = pricebookPrimaryPriceSuffix(priceSummaryItem);

  return (
    <TooltipProvider>
      <FormPageLayout
        title={duplicate ? 'Duplicate item' : `New ${ITEM_TYPE_LABELS[watchedType] ?? 'Item'}`}
        onBack={goBack}
        noscroll
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={goBack}
              disabled={createItem.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={form.handleSubmit((v) => onSubmit(v, { addAnother: true }), handleInvalid)}
              disabled={isMutating || checkingCategories}
              title={checkingCategories ? CHECKING_FOLDERS_HINT : undefined}
            >
              {isMutating && <Loader2 size={14} className="mr-1.5 animate-spin" />}
              Save &amp; add another
            </Button>
            <Button
              size="sm"
              onClick={form.handleSubmit((v) => onSubmit(v), handleInvalid)}
              disabled={isMutating || checkingCategories}
              title={checkingCategories ? CHECKING_FOLDERS_HINT : undefined}
            >
              {isMutating && <Loader2 size={14} className="mr-1.5 animate-spin" />}
              Create
            </Button>
          </>
        }
      >
        <div className="flex h-full min-h-0">
          <Form {...form}>
            <form
              ref={formRef}
              onSubmit={form.handleSubmit((v) => onSubmit(v), handleInvalid)}
              className="flex h-full min-h-0 min-w-0 flex-1"
            >
              <div ref={scrollBodyRef} className="min-w-0 flex-1 overflow-y-auto">
                <div className="space-y-4 px-4 py-4">
                  <PricebookItemMainSection
                    form={form}
                    itemType={watchedType}
                    namePlaceholder={
                      isService
                        ? 'e.g. AC Tune-Up'
                        : isMembership
                          ? 'e.g. Comfort Care Membership'
                          : isMaterial
                            ? 'e.g. Copper Pipe 1/2"'
                            : 'e.g. HVAC Bundle'
                    }
                    codePlaceholder={
                      isService
                        ? 'e.g. SVC-AC-001'
                        : isMembership
                          ? 'e.g. MEM-GOLD-001'
                          : 'e.g. MAT-CU-012'
                    }
                    codeHint="Auto-generated from name. You can edit it."
                    open={basicInfoOpen}
                    onToggle={() => setBasicInfoOpen((v) => !v)}
                    onPriceStrategyChange={handlePriceStrategyChange}
                    primarySupplierName={primarySupplierName}
                    draftSuppliers={draftSuppliers}
                    onDraftSuppliersChange={setDraftSuppliers}
                    tagIds={tagIds}
                    onTagIdsChange={setTagIds}
                    categoryIds={categoryIds}
                    onCategoryIdsChange={setCategoryIds}
                    imagePreview={imagePreview}
                    onSelectImage={(file) => void selectImage(file)}
                    onClearImage={clearImage}
                    header={
                      <div className="flex flex-wrap gap-1.5">
                        {(
                          [
                            {
                              type: 'service',
                              icon: <Wrench size={11} />,
                              label: 'Service',
                            },
                            {
                              type: 'material',
                              icon: <Package size={11} />,
                              label: 'Material',
                            },
                            {
                              type: 'equipment',
                              icon: <Zap size={11} />,
                              label: 'Equipment',
                            },
                            {
                              type: 'membership',
                              icon: <Crown size={11} />,
                              label: 'Membership',
                            },
                            {
                              type: 'bundle',
                              icon: <Boxes size={11} />,
                              label: 'Bundle',
                            },
                            {
                              type: 'fee',
                              icon: <Receipt size={11} />,
                              label: 'Fee',
                            },
                          ] as {
                            type: SelectableItemType;
                            icon: React.ReactNode;
                            label: string;
                          }[]
                        ).map(({ type: t, icon, label }) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => handleTypeChange(t)}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                              watchedType === t
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border bg-background text-muted-foreground hover:bg-muted'
                            }`}
                          >
                            {icon}
                            {label}
                          </button>
                        ))}
                      </div>
                    }
                  />

                  {isService && watchedPriceStrategy === 'DYNAMIC' && (
                    <>
                      <DynamicServicePricingEditor
                        value={dynamicPricingConfig}
                        onChange={setDynamicPricingConfig}
                      />
                      <ProfitUpliftEditor
                        value={dynamicPricingConfig}
                        onChange={setDynamicPricingConfig}
                        amount={dynamicSummary.surcharge}
                        base={dynamicSummary.surchargeBase}
                      />
                    </>
                  )}

                  {(hasComponents || isMembership) && (
                    <PricebookItemAdvancedSections
                      form={form}
                      itemType={watchedType}
                      isService={isService}
                      hasComponents={hasComponents}
                      isMembership={isMembership}
                      watchedTaxable={Boolean(watchedTaxable)}
                      components={components}
                      onComponentsChange={setComponents}
                      componentItemCache={componentItemCache}
                      onComponentItemCacheChange={setComponentItemCache}
                      materialMarkupProfileId={dynamicPricingConfig.materialMarkupProfileId}
                      equipmentMarkupProfileId={dynamicPricingConfig.equipmentMarkupProfileId}
                      materialPricingMethod={dynamicPricingConfig.materialPricingMethod}
                      equipmentPricingMethod={dynamicPricingConfig.equipmentPricingMethod}
                      useSameMarkupProfile={dynamicPricingConfig.useSameMarkupProfile}
                      onMaterialMarkupProfileChange={(
                        materialMarkupProfileId,
                        materialPricingMethod,
                      ) =>
                        setDynamicPricingConfig((current) => ({
                          ...current,
                          materialMarkupProfileId,
                          materialPricingMethod,
                        }))
                      }
                      onEquipmentMarkupProfileChange={(
                        equipmentMarkupProfileId,
                        equipmentPricingMethod,
                      ) =>
                        setDynamicPricingConfig((current) => ({
                          ...current,
                          equipmentMarkupProfileId,
                          equipmentPricingMethod,
                        }))
                      }
                      onUseSameMarkupProfileChange={(useSameMarkupProfile) =>
                        setDynamicPricingConfig((current) => ({
                          ...current,
                          useSameMarkupProfile,
                        }))
                      }
                      upsellItems={upsellItems}
                      onUpsellItemsChange={setUpsellItems}
                      componentsSectionOpen={componentsSectionOpen}
                      onComponentsSectionToggle={() => setComponentsSectionOpen((v) => !v)}
                      membershipLinkOpen={membershipLinkOpen}
                      onMembershipLinkToggle={() => setMembershipLinkOpen((v) => !v)}
                      taxOpen={taxOpen}
                      onTaxToggle={() => setTaxOpen((v) => !v)}
                      aiOpen={aiOpen}
                      onAiToggle={() => setAiOpen((v) => !v)}
                      displayOpen={displayOpen}
                      onDisplayToggle={() => setDisplayOpen((v) => !v)}
                      includeOptionalSections={false}
                    />
                  )}

                  {isService && watchedPriceStrategy === 'DYNAMIC' && (
                    <AdvancedServiceAdjustments
                      value={dynamicPricingConfig}
                      onChange={setDynamicPricingConfig}
                    />
                  )}

                  <PricebookItemAdvancedSections
                    form={form}
                    itemType={watchedType}
                    isService={isService}
                    hasComponents={hasComponents}
                    isMembership={isMembership}
                    watchedTaxable={Boolean(watchedTaxable)}
                    components={components}
                    onComponentsChange={setComponents}
                    componentItemCache={componentItemCache}
                    onComponentItemCacheChange={setComponentItemCache}
                    materialMarkupProfileId={dynamicPricingConfig.materialMarkupProfileId}
                    equipmentMarkupProfileId={dynamicPricingConfig.equipmentMarkupProfileId}
                    materialPricingMethod={dynamicPricingConfig.materialPricingMethod}
                    equipmentPricingMethod={dynamicPricingConfig.equipmentPricingMethod}
                    useSameMarkupProfile={dynamicPricingConfig.useSameMarkupProfile}
                    onMaterialMarkupProfileChange={(
                      materialMarkupProfileId,
                      materialPricingMethod,
                    ) =>
                      setDynamicPricingConfig((current) => ({
                        ...current,
                        materialMarkupProfileId,
                        materialPricingMethod,
                      }))
                    }
                    onEquipmentMarkupProfileChange={(
                      equipmentMarkupProfileId,
                      equipmentPricingMethod,
                    ) =>
                      setDynamicPricingConfig((current) => ({
                        ...current,
                        equipmentMarkupProfileId,
                        equipmentPricingMethod,
                      }))
                    }
                    onUseSameMarkupProfileChange={(useSameMarkupProfile) =>
                      setDynamicPricingConfig((current) => ({
                        ...current,
                        useSameMarkupProfile,
                      }))
                    }
                    upsellItems={upsellItems}
                    onUpsellItemsChange={setUpsellItems}
                    componentsSectionOpen={componentsSectionOpen}
                    onComponentsSectionToggle={() => setComponentsSectionOpen((v) => !v)}
                    membershipLinkOpen={membershipLinkOpen}
                    onMembershipLinkToggle={() => setMembershipLinkOpen((v) => !v)}
                    taxOpen={taxOpen}
                    onTaxToggle={() => setTaxOpen((v) => !v)}
                    aiOpen={aiOpen}
                    onAiToggle={() => setAiOpen((v) => !v)}
                    displayOpen={displayOpen}
                    onDisplayToggle={() => setDisplayOpen((v) => !v)}
                    includeRequiredSections={false}
                  />
                </div>
              </div>

              <FormSidebar
                checklist={pricebookItemChecklist({
                  typeDone: Boolean(watchedType),
                  name: watchedName,
                  code: watchedCode,
                  equipmentCategory: {
                    isEquipment: watchedType === 'equipment',
                    equipmentDescriptionId: watchedEquipmentDescriptionId,
                    equipmentCategoryKey: watchedEquipmentCategoryKey,
                    writesEquipmentType: true,
                  },
                  isMembership,
                  membershipPlanId: watchedMembershipPlanId,
                  hasOptionalDetails,
                })}
              >
                <FormSidebarSection label="Summary">
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="text-muted-foreground">Type</span>
                      <span>{ITEM_TYPE_LABELS[watchedType]}</span>
                    </div>
                    {isService && isTimeAndMaterials ? (
                      <>
                        {/* The same panel the estimate shows, so a service
                            priced here and the same service quoted on a
                            document read as one idea rather than two. */}
                        <DocumentCostPanel
                          className="border-0 bg-transparent p-0"
                          subtotal={dynamicSummary.calculatedPrice}
                          taxTotal={0}
                          grandTotal={dynamicSummary.calculatedPrice}
                          breakdown={{
                            labor: dynamicSummary.laborCost,
                            laborHours: Math.round((dynamicSummary.totalMinutes / 60) * 100) / 100,
                            material: dynamicSummary.materialCost,
                            equipment: dynamicSummary.equipmentCost,
                          }}
                        />
                        <SummaryRow
                          label="Labor"
                          value={fmt(dynamicSummary.labor)}
                          suffix={`${Math.floor(dynamicSummary.totalMinutes / 60)}h ${dynamicSummary.totalMinutes % 60}m`}
                        />
                        {/* Every part contributes to the calculated price,
                            whether the service covers it or bills it extra —
                            so this figure is "parts", not "included parts". */}
                        <SummaryRow label="Parts" value={fmt(dynamicSummary.parts)} />
                        <SummaryRow
                          label="Calculated cost"
                          value={fmt(dynamicSummary.calculatedCost)}
                          muted
                        />
                        {dynamicSummary.aggregateMarkup != null && (
                          <SummaryRow
                            label="Parts markup"
                            value={`${dynamicSummary.aggregateMarkup.toFixed(1)}%`}
                            muted
                          />
                        )}
                        {dynamicSummary.complexity > 0 && (
                          <SummaryRow
                            label="Complexity"
                            value={fmt(dynamicSummary.complexity)}
                            muted
                          />
                        )}
                        {dynamicSummary.surcharge !== 0 && (
                          <SummaryRow
                            label="Profit uplift"
                            value={fmt(dynamicSummary.surcharge)}
                            muted
                          />
                        )}
                        <div className="mt-2 flex justify-between gap-3 border-t pt-2 font-semibold">
                          <span>Calculated price</span>
                          <span className="tabular-nums">
                            {fmt(dynamicSummary.calculatedPrice)}
                          </span>
                        </div>

                        {/* WHAT-IF, not configuration. Both rules are already
                            switched on or off in the pricing editor; these ask
                            the different question of what they DO, which the
                            everyday price cannot show because neither applies
                            to an everyday job. Nothing here is saved, and the
                            Calculated price above never moves. */}
                        <div className="mt-3 space-y-2 border-t pt-3">
                          {/* Styled as the Cost and Margin groups are: an
                              uppercase group label over plain rows. The dashed
                              box it replaced read as a drop zone rather than a
                              part of the summary. */}
                          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            Scenarios
                          </p>
                          <label className="flex items-center justify-between gap-3 text-xs">
                            <span
                              className={
                                dynamicPricingConfig.afterHours?.enabled
                                  ? ''
                                  : 'text-muted-foreground'
                              }
                            >
                              …the work is after hours
                              {!dynamicPricingConfig.afterHours?.enabled && ' (not set up)'}
                            </span>
                            <Switch
                              aria-label="Preview after-hours pricing"
                              disabled={!dynamicPricingConfig.afterHours?.enabled}
                              checked={previewAfterHours}
                              onCheckedChange={setPreviewAfterHours}
                            />
                          </label>
                          <label className="flex items-center justify-between gap-3 text-xs">
                            <span
                              className={
                                dynamicPricingConfig.addOnDiscount?.enabled
                                  ? ''
                                  : 'text-muted-foreground'
                              }
                            >
                              …it is sold as an add-on
                              {!dynamicPricingConfig.addOnDiscount?.enabled && ' (not set up)'}
                            </span>
                            <Switch
                              aria-label="Preview add-on discount"
                              disabled={!dynamicPricingConfig.addOnDiscount?.enabled}
                              checked={previewAsAddOn}
                              onCheckedChange={setPreviewAsAddOn}
                            />
                          </label>

                          {previewAfterHours || previewAsAddOn ? (
                            <div className="space-y-1 border-t pt-2">
                              {adjustmentPreview.afterHoursAdjustment !== 0 && (
                                <SummaryRow
                                  label="After hours"
                                  value={`+${fmt(adjustmentPreview.afterHoursAdjustment)}`}
                                  muted
                                />
                              )}
                              {adjustmentPreview.addOnDiscount !== 0 && (
                                <SummaryRow
                                  label="Add-on discount"
                                  value={`-${fmt(adjustmentPreview.addOnDiscount)}`}
                                  muted
                                />
                              )}
                              <div className="flex justify-between gap-3 font-semibold">
                                <span>Price in that case</span>
                                <span className="tabular-nums">{fmt(adjustmentPreview.total)}</span>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </>
                    ) : isService ? (
                      <>
                        {/* PRICE FIRST, then what it costs and what is left.

                            Two different kinds of statement: what the CUSTOMER PAYS

                            (this service's price, the parts billed beside it, their

                            total) and what the BUSINESS KEEPS (cost, margin, net).

                            Drawn the other way round, Gross margin and Net amount sat

                            in the middle of the price composition and the column read

                            as one undifferentiated list of money. */}
                        <PricebookBundleSummary
                          listedPrice={listedPrice}
                          listedPriceLabel={listedPriceLabel}
                          listedPriceSuffix={listedPriceSuffix}
                          totals={componentTotals}
                          showBundleTotal={showBundleTotal}
                          totalLabel="Total (service + parts)"
                        />

                        {/* The same panel a T&M service gets: how a service is priced

                            changes the figures, not what the operator needs to see.

                            Its own price rows are suppressed because the summary above

                            already states them — and better, since it breaks them

                            down. Margin is still taken against that same total. */}

                        <DocumentCostPanel
                          className="border-0 bg-transparent p-0"
                          showPriceSummary={false}
                          subtotal={componentTotals.bundleTotal}
                          taxTotal={0}
                          grandTotal={componentTotals.bundleTotal}
                          breakdown={flatServiceCost}
                        />
                      </>
                    ) : hasComponents ? (
                      <PricebookBundleSummary
                        listedPrice={listedPrice}
                        listedPriceLabel={listedPriceLabel}
                        listedPriceSuffix={listedPriceSuffix}
                        totals={componentTotals}
                        showBundleTotal={showBundleTotal}
                        totalLabel={isBundle ? 'Bundle total' : 'Total (service + parts)'}
                      />
                    ) : !isMembership ? (
                      <div className="flex justify-between gap-3">
                        <span className="text-muted-foreground">{listedPriceLabel}</span>
                        <span className="tabular-nums">
                          {fmt(listedPrice)}
                          {listedPriceSuffix && (
                            <span className="ml-0.5 text-xs text-muted-foreground">
                              {listedPriceSuffix}
                            </span>
                          )}
                        </span>
                      </div>
                    ) : null}
                    <div className="flex justify-between gap-3">
                      <span className="text-muted-foreground">Tags</span>
                      <span>{tagIds.length}</span>
                    </div>
                    {hasComponents && components.length > 0 && (
                      <div className="flex justify-between gap-3">
                        <span className="text-muted-foreground">Parts</span>
                        <span>{components.length}</span>
                      </div>
                    )}
                    {isMembership && (
                      <div className="flex justify-between gap-3">
                        <span className="text-muted-foreground">Plan</span>
                        <span>{watchedMembershipPlanId ? 'Linked' : '—'}</span>
                      </div>
                    )}
                  </div>
                </FormSidebarSection>
                {!isMembership && !hasComponents && (
                  <FormSidebarSection label="Member Pricing" border>
                    <MembershipPricingBreakdown basePrice={listedPrice} itemType={watchedType} />
                  </FormSidebarSection>
                )}
              </FormSidebar>
            </form>
          </Form>
        </div>
        <UnsavedChangesDialog
          open={unsavedChanges.isBlocked}
          onStay={unsavedChanges.stay}
          onLeave={unsavedChanges.leave}
          description="This item has not been created yet. Leaving now will discard it."
        />
      </FormPageLayout>
    </TooltipProvider>
  );
}

function SummaryRow({
  label,
  value,
  suffix,
  muted,
}: {
  label: string;
  value: string;
  suffix?: string;
  muted?: boolean;
}) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={muted ? 'tabular-nums text-muted-foreground' : 'tabular-nums'}>
        {value}
        {suffix && <span className="ml-1 text-xs text-muted-foreground">({suffix})</span>}
      </span>
    </div>
  );
}
